import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useApi from "../../../CustomHooks/useApi";
import { useSelector } from "react-redux";
import { AIR, Readout, StatusBadge, AirFontImport, apiErrorMessage, isTicketingInProgress } from "./airTheme";

const fmtTime = (dt) => (dt ? dt.slice(11, 16) || dt : "--");

// Air_Ticketing's per-PNR Status_Id (11/22/33) maps onto the shared
// departure-board status vocabulary used across the whole Air flow.
const TICKETING_STATUS_KEY = { 11: "CONFIRMED", 22: "TICKETING_IN_PROGRESS", 33: "TICKETING_FAILED" };

const FlightConfirm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mid = useSelector((state) => state.auth.employeeDetails?.username);

  const {
    bookingRefNo = "",
    repricedFlights = [],
    totalAmount = 0,
    searchForm = {},
    passengers = [],
    contact = {},
    provider = "weflygo",
    nextraBookingKey = null,
    nextraDomInt = null,
    paxCounts = {},
    nextraSelectedAddons = [],
    tripType = 0,
  } = location.state || {};

  const { callApi: addPaymentApi, loading: paying } = useApi("/air/add-payment", "POST");
  const { callApi: ticketingApi, loading: ticketing } = useApi("/air/ticketing", "POST");
  const { callApi: nextraBookApi, loading: nextraBooking } = useApi("/air/nextra/book", "POST");

  const [mpin, setMpin] = useState("");
  const [mpinFocused, setMpinFocused] = useState(false);
  const [step, setStep] = useState("confirm"); // "confirm" | "pending" | "success" | "failed"
  const [error, setError] = useState("");
  const [ticketResult, setTicketResult] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [nextraTxid, setNextraTxid] = useState(null);

  const isLoading = paying || ticketing || nextraBooking;

  // Nextra's /book both charges the wallet and confirms the ticket in a single call — no
  // separate AddPayment/Ticketing steps, and no pre-existing bookingRefNo to check for (Nextra
  // only generates one once /book actually runs). See NextraBookService.book() on the backend:
  // a clean failure rolls the wallet back itself before returning (safe to retry here), while a
  // network/timeout error leaves the booking "pending" with no automatic refund (same rule as
  // the WeFlyGo ambiguous-outcome case) and no ref code the frontend can show yet either.
  const handleConfirmNextraPayment = async () => {
    if (!mpin || mpin.length < 4) return setError("Enter your 4-6 digit MPIN");

    // Nextra's docs specify dob as YYYY-MM-DD — p.dobInput is the raw value straight from the
    // <input type="date"> in FlightBooking.js; p.dob is WeFlyGo's own MM/DD/YYYY conversion of
    // the same value (via toDobApi) and must not be reused here.
    const nextraPassengers = passengers.map((p) => ({
      title: p.title,
      first_name: p.firstName,
      last_name: p.lastName,
      dob: p.dobInput || "",
      type: p.paxType === 0 ? "Adult" : p.paxType === 1 ? "Child" : "Infant",
    }));

    // Seats/add-ons were picked on the previous screen (against paxId only — no name yet at
    // that point) — now that real passenger names exist, turn each selection into the actual
    // NextraSSRRow shape /book expects. Per Nextra's docs, ssrList's own paxtype uses the
    // abbreviated ADT/CHD/INF vocabulary — a different convention from passengerList's own
    // full-word "Adult"/"Child"/"Infant" above, not a typo.
    const nextraSsrRows = nextraSelectedAddons.map((sel) => {
      const pax = passengers.find((p) => p.paxId === sel.paxId) || {};
      return {
        flightNumber: sel.flightNumber, carrier: sel.carrier,
        origin: sel.origin, destination: sel.destination, jtype: sel.jtype,
        passenger_fname: pax.firstName, passenger_lname: pax.lastName, passenger_title: pax.title,
        paxid: String(sel.paxId),
        paxtype: pax.paxType === 0 ? "ADT" : pax.paxType === 1 ? "CHD" : "INF",
        ssr_chargeableamount: sel.ssr_chargeableamount,
        ssrcode: sel.ssrcode, ssrname: sel.ssrname, ssrtype: sel.ssrtype, ssrid: sel.ssrid,
      };
    });

    // Same shape/purpose as WeFlyGo's own flightsMeta (FlightBooking.js) — Nextra's /book
    // response never echoes route/airline/time info back, so this is the only place that data
    // survives into travel_booking for FlightHistory.js's route strip / flight label to read.
    const nextraFlightsMeta = repricedFlights.map((f, i) => ({
      trip_index: i,
      airline_code: f.Airline_Code,
      flight_numbers: (f.Segments || []).map((s) => s.Flight_Number).filter(Boolean).join(" · "),
      origin: f.Segments?.[0]?.Origin,
      destination: f.Segments?.[f.Segments.length - 1]?.Destination,
      departure_dt: f.Segments?.[0]?.Departure_DateTime,
      arrival_dt: f.Segments?.[f.Segments.length - 1]?.Arrival_DateTime,
      fare_amount: (f.Fares?.[0]?.FareDetails || []).reduce((s, fd) => s + (fd.Total_Amount || 0), 0),
    }));

    try {
      const res = await nextraBookApi({
        mid, mpin,
        taxInfoStr: nextraBookingKey,
        leadEmail: contact.email,
        leadMobile: contact.mobile,
        leadCountry: "IN",
        leadCity: "",
        leadState: "",
        paymentMode: "cashcard",
        domInt: nextraDomInt || "domestic",
        promoCode: "",
        adultCount: paxCounts.adults || 1,
        childCount: paxCounts.children || 0,
        infantCount: paxCounts.infants || 0,
        passengers: nextraPassengers,
        ssrRows: nextraSsrRows,
        origin: searchForm.from?.toUpperCase(),
        destination: searchForm.to?.toUpperCase(),
        travelDate: searchForm.travelDate,
        totalAmount,
        tripType,
        flightsMeta: nextraFlightsMeta,
      });

      // Business-logic failures land here as a normal (HTTP 202) response, not a thrown
      // error — see GlobalExceptionHandler's catch-all RuntimeException handler on the backend.
      if (res?.message) {
        if (res.message.startsWith("Nextra Book outcome unknown")) {
          setStep("pending");
          return;
        }
        // A clean "Nextra Book failed: ..." — the backend already rolled the wallet debit back
        // before returning this. Always land on a terminal screen here rather than leaving the
        // error inline on the pay form — same reasoning as the pending/success cases below.
        setError(res.message);
        setStep("failed");
        return;
      }

      const txid = res?.ApiStatus?.Result;
      if (!res?.ApiStatus || !txid) {
        setError("Booking failed. Please try again.");
        setStep("failed");
        return;
      }
      setNextraTxid(txid);
      setStep("success");
    } catch (e) {
      // A real network/HTTP-level failure — the backend's own RuntimeException handling above
      // already returns a normal 202 response, so this only fires for something outside that
      // (connection drop, etc.) where the wallet outcome genuinely can't be told from here — same
      // "unknown, don't retry, don't assume charged-or-not" situation as the pending case above.
      setError(e?.response?.data?.message || e.message || "Booking failed. Contact support if your wallet was charged.");
      setStep("pending");
    }
  };

  const handleConfirmPayment = async () => {
    setError("");
    if (!mpin || mpin.length < 4) return setError("Enter your 4-6 digit MPIN");

    if (provider === "nextra") {
      return handleConfirmNextraPayment();
    }

    if (!bookingRefNo) return setError("Invalid booking reference. Please restart.");

    // Plain local flag, not React state — needs to be readable synchronously inside the catch
    // block below to tell a pre-payment failure (safe to retry) from a post-payment one (wallet
    // already charged, must never show the Pay button again).
    let paymentSucceeded = false;

    try {
      // Step 1: Add Payment (deduct wallet)
      const payRes = await addPaymentApi({
        refNo: bookingRefNo,
        productId: 1,
        transactionType: 0,
        clientRefNo: bookingRefNo,
        mpin,
        mid,
      });

      const payError = apiErrorMessage(payRes);
      if (payError) {
        return setError(payError);
      }
      if (!payRes?.Response_Header) {
        return setError("Payment failed. Please try again.");
      }
      setPaymentResult(payRes);
      paymentSucceeded = true;

      // Step 2: Issue Ticket
      const ticketRes = await ticketingApi({
        bookingRefNo,
        ticketingType: 1,
      });

      // Payment already succeeded at this point — never fall back to the payment form again,
      // even if ticketing's outcome is still unknown. Show a distinct "pending" screen instead.
      if (isTicketingInProgress(ticketRes)) {
        setStep("pending");
        return;
      }

      const ticketError = apiErrorMessage(ticketRes);
      if (ticketError) {
        setError(ticketError);
        setStep("failed");
        return;
      }
      if (!ticketRes?.AirlinePNRDetails?.length) {
        setError("Ticketing failed.");
        setStep("failed");
        return;
      }

      setTicketResult(ticketRes);
      setStep("success");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Payment or ticketing failed. Contact support.");
      if (paymentSucceeded) {
        // Wallet was already charged before this exception hit (a network/server error on the
        // ticketing call itself, not a clean failure response) — outcome unknown, same as the
        // isTicketingInProgress case above. Never re-show the Pay button here.
        setStep("pending");
      }
    }
  };

  // ─── Pending screen — payment done, ticketing outcome not known yet ───
  // No Pay button here on purpose: charging the merchant again would be a real money bug.
  if (step === "pending") {
    return (
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "16px 12px" }}>
        <AirFontImport />
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: AIR.amberSoft, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 32 }}>
            ⏳
          </div>
          <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 900, color: AIR.amberDeep }}>Payment received — ticket processing</h2>
          <p style={{ margin: 0, fontSize: 14, color: "#6B7280", maxWidth: 440, marginLeft: "auto", marginRight: "auto" }}>
            Your wallet has already been charged. The airline hasn't confirmed the ticket yet — this
            usually resolves within a few minutes. We'll keep checking automatically; no action needed.
          </p>
        </div>

        <div style={{ background: AIR.amberSoft, border: `1.5px solid #FDE9C8`, borderRadius: 14, padding: "16px 20px", marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              {/* Nextra's ambiguous-outcome case has no ref code yet — it's generated inside
                  /book, which is exactly the call whose outcome is unknown here. */}
              <div style={{ fontSize: 11, fontWeight: 700, color: AIR.amberDeep, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {bookingRefNo ? "Booking Reference" : "Booking Status"}
              </div>
              {bookingRefNo
                ? <Readout size={20} weight={700} color={AIR.navy} tracking="0.04em">{bookingRefNo}</Readout>
                : <div style={{ fontSize: 13, color: AIR.navy, marginTop: 2 }}>Check My Bookings in a few minutes for the confirmed reference.</div>
              }
            </div>
            <StatusBadge status="TICKETING_IN_PROGRESS" />
          </div>
        </div>

        {error && (
          <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, padding: "12px 16px", marginBottom: 16, color: "#92400E", fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => navigate("/flight-history")}
            style={{ flex: 1, height: 46, borderRadius: 10, border: `1.5px solid ${AIR.navy}`, background: "#fff", color: AIR.navy, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            Check Status in History
          </button>
          <button
            onClick={() => navigate("/flight-search")}
            style={{ flex: 1, height: 46, borderRadius: 10, border: "none", background: AIR.navy, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            Book Another
          </button>
        </div>
      </div>
    );
  }

  // ─── Failed screen (Nextra) — a clean "Nextra Book failed: ..." response. Unlike WeFlyGo's
  // failed case, the backend already rolled the wallet debit back SYNCHRONOUSLY inside the same
  // /book call (see NextraBookService.book()) — there's no background scheduler catching up
  // later, so the messaging here must not say "will be refunded" like WeFlyGo's screen does.
  if (step === "failed" && provider === "nextra") {
    return (
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "16px 12px" }}>
        <AirFontImport />
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 32 }}>
            ✕
          </div>
          <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 900, color: "#B91C1C" }}>Booking failed</h2>
          <p style={{ margin: 0, fontSize: 14, color: "#6B7280", maxWidth: 440, marginLeft: "auto", marginRight: "auto" }}>
            Your wallet was not charged for this attempt — any amount debited was refunded
            automatically. You can search again and try booking once more.
          </p>
        </div>

        {error && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 16, color: "#DC2626", fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => navigate("/flight-history")}
            style={{ flex: 1, height: 46, borderRadius: 10, border: `1.5px solid ${AIR.navy}`, background: "#fff", color: AIR.navy, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            Check History
          </button>
          <button
            onClick={() => navigate("/flight-search")}
            style={{ flex: 1, height: 46, borderRadius: 10, border: "none", background: AIR.navy, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            Book Another
          </button>
        </div>
      </div>
    );
  }

  // ─── Failed screen — payment done, ticketing genuinely failed at the airline ───
  // No Pay button here either: retrying would just double-charge in front of the merchant, and
  // the backend already refunds automatically (AirBookingStatusScheduler) once it re-verifies.
  if (step === "failed") {
    return (
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "16px 12px" }}>
        <AirFontImport />
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 32 }}>
            ✕
          </div>
          <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 900, color: "#B91C1C" }}>Ticketing failed</h2>
          <p style={{ margin: 0, fontSize: 14, color: "#6B7280", maxWidth: 440, marginLeft: "auto", marginRight: "auto" }}>
            Your wallet was charged, but the airline could not confirm the ticket. Your payment will
            be refunded automatically — no action needed. This usually reflects within 15-20 minutes.
          </p>
        </div>

        <div style={{ background: "#FEF2F2", border: "1.5px solid #FECACA", borderRadius: 14, padding: "16px 20px", marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#B91C1C", textTransform: "uppercase", letterSpacing: 0.5 }}>Booking Reference</div>
              <Readout size={20} weight={700} color={AIR.navy} tracking="0.04em">{bookingRefNo}</Readout>
            </div>
            <StatusBadge status="TICKETING_FAILED" />
          </div>
        </div>

        {error && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 16, color: "#DC2626", fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => navigate("/flight-history")}
            style={{ flex: 1, height: 46, borderRadius: 10, border: `1.5px solid ${AIR.navy}`, background: "#fff", color: AIR.navy, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            Check Status in History
          </button>
          <button
            onClick={() => navigate("/flight-search")}
            style={{ flex: 1, height: 46, borderRadius: 10, border: "none", background: AIR.navy, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            Book Another
          </button>
        </div>
      </div>
    );
  }

  // ─── Success screen (Nextra) ───────────────────────────────────
  // Nextra's /book response only ever returns ApiStatus.Result (its own txid, e.g.
  // "FB2605MRM35P") — that's the same identifier used for every later call (eticket, cancel,
  // reschedule...), so it's shown here as the booking reference rather than fabricating one.
  // No per-leg PNR breakdown like WeFlyGo's AirlinePNRDetails — Nextra's /book doesn't return one.
  if (step === "success" && provider === "nextra" && nextraTxid) {
    return (
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "16px 12px" }}>
        <AirFontImport />
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 36 }}>
            ✓
          </div>
          <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 900, color: "#059669" }}>Booking Confirmed!</h2>
          <p style={{ margin: 0, fontSize: 14, color: "#6B7280" }}>Your ticket has been issued successfully</p>
        </div>

        <div style={{ background: "#F0FDF4", border: "1.5px solid #BBF7D0", borderRadius: 14, padding: "16px 20px", marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#059669", textTransform: "uppercase", letterSpacing: 0.5 }}>Booking Reference</div>
              <Readout size={20} weight={700} color={AIR.navy} tracking="0.04em">{nextraTxid}</Readout>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "#6B7280" }}>Amount Paid</div>
              <Readout size={18} weight={700} color={AIR.navy}>₹{totalAmount.toLocaleString("en-IN")}</Readout>
            </div>
          </div>
        </div>

        {repricedFlights.map((f, i) => {
          const segs = f.Segments || [];
          const first = segs[0];
          const last = segs[segs.length - 1];
          return (
            <div key={i} style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 14, padding: "16px 18px", marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#1E3A5F" }}>
                  {first?.Origin} → {last?.Destination}
                </div>
                <StatusBadge status="CONFIRMED" />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
                <div style={{ textAlign: "center" }}>
                  <Readout size={18} weight={700}>{fmtTime(first?.Departure_DateTime)}</Readout>
                  <div style={{ fontSize: 11, color: "#6B7280" }}>{first?.Origin}</div>
                </div>
                <div style={{ flex: 1, borderTop: "1.5px dashed #E5E7EB" }} />
                <div style={{ textAlign: "center" }}>
                  <Readout size={18} weight={700}>{fmtTime(last?.Arrival_DateTime)}</Readout>
                  <div style={{ fontSize: 11, color: "#6B7280" }}>{last?.Destination}</div>
                </div>
              </div>
            </div>
          );
        })}

        {passengers.length > 0 && (
          <div style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#6B7280", marginBottom: 10 }}>PASSENGERS</div>
            {passengers.map((p, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < passengers.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                <span style={{ fontSize: 14, color: "#111827" }}>{p.title} {p.firstName} {p.lastName}</span>
                <span style={{ fontSize: 12, color: "#9CA3AF" }}>{["Adult", "Child", "Infant"][p.paxType]}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => navigate("/flight-history")}
            style={{ flex: 1, height: 46, borderRadius: 10, border: "1.5px solid #1E3A5F", background: "#fff", color: "#1E3A5F", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            View History
          </button>
          <button
            onClick={() => navigate("/flight-search")}
            style={{ flex: 1, height: 46, borderRadius: 10, border: "none", background: "#1E3A5F", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            Book Another
          </button>
        </div>
      </div>
    );
  }

  // ─── Success screen ───────────────────────────────────────────
  if (step === "success" && ticketResult) {
    const pnrDetails = ticketResult.AirlinePNRDetails || [];
    return (
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "16px 12px" }}>
        <AirFontImport />
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 36 }}>
            ✓
          </div>
          <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 900, color: "#059669" }}>Booking Confirmed!</h2>
          <p style={{ margin: 0, fontSize: 14, color: "#6B7280" }}>Tickets have been issued successfully</p>
        </div>

        {/* Booking reference */}
        <div style={{ background: "#F0FDF4", border: "1.5px solid #BBF7D0", borderRadius: 14, padding: "16px 20px", marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#059669", textTransform: "uppercase", letterSpacing: 0.5 }}>Booking Reference</div>
              <Readout size={20} weight={700} color={AIR.navy} tracking="0.04em">{ticketResult.Booking_RefNo || bookingRefNo}</Readout>
            </div>
            {paymentResult && (
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: "#6B7280" }}>Amount Paid</div>
                <Readout size={18} weight={700} color={AIR.navy}>₹{paymentResult.Amount?.toLocaleString("en-IN") || totalAmount.toLocaleString("en-IN")}</Readout>
              </div>
            )}
          </div>
        </div>

        {/* PNR details per flight */}
        {pnrDetails.map((pnrDetail, i) => (
          <div key={i} style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 14, padding: "16px 18px", marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#1E3A5F" }}>
                {repricedFlights[i] ? `${(repricedFlights[i].Segments || [])[0]?.Origin} → ${(repricedFlights[i].Segments || [])[(repricedFlights[i].Segments || []).length - 1]?.Destination}` : `Flight ${i + 1}`}
              </div>
              <StatusBadge status={TICKETING_STATUS_KEY[pnrDetail.Status_Id]} />
            </div>

            {pnrDetail.Status_Id === 22 && pnrDetail.Failure_Remark && (
              <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#92400E" }}>
                ⚠ {pnrDetail.Failure_Remark}
              </div>
            )}

            {(pnrDetail.AirlinePNRs || []).map((pnr, j) => (
              <div key={j} style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginBottom: 8 }}>
                <div style={{ background: "#F8FAFC", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: "#9CA3AF" }}>Airline PNR</div>
                  <Readout size={18} weight={700} color={AIR.navy} tracking="0.08em">{pnr.Airline_PNR}</Readout>
                  <div style={{ fontSize: 11, color: "#6B7280" }}>{pnr.Airline_Code}</div>
                </div>
                {pnr.CRS_PNR && (
                  <div style={{ background: "#F8FAFC", borderRadius: 8, padding: "10px 14px" }}>
                    <div style={{ fontSize: 11, color: "#9CA3AF" }}>CRS PNR</div>
                    <Readout size={18} weight={700} color={AIR.navy} tracking="0.08em">{pnr.CRS_PNR}</Readout>
                    <div style={{ fontSize: 11, color: "#6B7280" }}>{pnr.CRS_Code}</div>
                  </div>
                )}
              </div>
            ))}

            {pnrDetail.Hold_Validity && (
              <div style={{ fontSize: 12, color: "#D97706" }}>Hold valid until: {pnrDetail.Hold_Validity}</div>
            )}
          </div>
        ))}

        {/* Passengers */}
        {passengers.length > 0 && (
          <div style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#6B7280", marginBottom: 10 }}>PASSENGERS</div>
            {passengers.map((p, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < passengers.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                <span style={{ fontSize: 14, color: "#111827" }}>{p.title} {p.firstName} {p.lastName}</span>
                <span style={{ fontSize: 12, color: "#9CA3AF" }}>{["Adult", "Child", "Infant"][p.paxType]}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => navigate("/flight-history")}
            style={{ flex: 1, height: 46, borderRadius: 10, border: "1.5px solid #1E3A5F", background: "#fff", color: "#1E3A5F", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            View History
          </button>
          <button
            onClick={() => navigate("/flight-search")}
            style={{ flex: 1, height: 46, borderRadius: 10, border: "none", background: "#1E3A5F", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            Book Another
          </button>
        </div>
      </div>
    );
  }

  // ─── Confirm screen ────────────────────────────────────────────
  // Nextra never has a pre-existing bookingRefNo here — it only generates one inside /book,
  // which hasn't run yet on this screen — so the emptiness check only applies to WeFlyGo.
  if (provider === "nextra" ? !repricedFlights.length : !bookingRefNo) {
    return (
      <div style={{ textAlign: "center", padding: 60, color: "#9CA3AF" }}>
        <div>No booking found. <button onClick={() => navigate("/flight-search")} style={{ color: "#3B82F6", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Start over</button></div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "16px 12px" }}>
      <AirFontImport />
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "1.5px solid #E5E7EB", cursor: "pointer", padding: "6px 14px", borderRadius: 8, color: "#374151", fontSize: 13, fontWeight: 600 }}>
          ← Back
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#1E3A5F" }}>Confirm &amp; Pay</h2>
          <div style={{ fontSize: 12, color: "#9CA3AF" }}>Review your booking before payment</div>
        </div>
      </div>

      {/* Booking ref — Nextra doesn't have one yet at this stage, only WeFlyGo does */}
      {bookingRefNo && (
        <div style={{ background: "#F0F9FF", border: "1.5px solid #BAE6FD", borderRadius: 12, padding: "12px 18px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: AIR.sky, textTransform: "uppercase", letterSpacing: 0.5 }}>Booking Reference</div>
          <Readout size={20} weight={700} color={AIR.navy} tracking="0.04em">{bookingRefNo}</Readout>
          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>Keep this reference if you need to contact support</div>
        </div>
      )}

      {/* Flight summary */}
      {repricedFlights.map((f, i) => {
        const segs = f.Segments || [];
        const first = segs[0];
        const last = segs[segs.length - 1];
        const fare = (f.Fares || [])[0];
        // Nextra's FareDetails.Total_Amount is already the grand total for every passenger
        // searched (see the matching note in FlightSSRSeat.js). WeFlyGo's FareDetails is PER PAX
        // TYPE (PAX_Type: 0=Adult/1=Child/2=Infant, per AirFareDetail.java) — each entry needs
        // multiplying by ONLY its own type's count, not summed-then-labeled "/pax" (that summed
        // every type's rate together, which isn't any one passenger's actual fare).
        const legAmount = provider === "nextra"
          ? (fare?.FareDetails || []).reduce((s, fd) => s + (fd.Total_Amount || 0), 0)
          : (fare?.FareDetails || []).reduce((s, fd) => {
              const countByType = [paxCounts.adults || 0, paxCounts.children || 0, paxCounts.infants || 0];
              return s + (fd.Total_Amount || 0) * (countByType[fd.PAX_Type] ?? 0);
            }, 0);
        return (
          <div key={i} style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 14, padding: "16px 18px", marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", marginBottom: 10 }}>
              {repricedFlights.length > 1 ? (i === 0 ? "Onward" : "Return") : "Flight"}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>{first?.Airline_Name || f.Airline_Code}</div>
                <div style={{ fontSize: 12, color: "#9CA3AF" }}>{f.Flight_Numbers}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <Readout size={14} weight={700} color={AIR.navy}>₹{legAmount.toLocaleString("en-IN")}</Readout>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
              <div style={{ textAlign: "center" }}>
                <Readout size={18} weight={700}>{fmtTime(first?.Departure_DateTime)}</Readout>
                <div style={{ fontSize: 11, color: "#6B7280" }}>{first?.Origin}</div>
              </div>
              <div style={{ flex: 1, borderTop: "1.5px dashed #E5E7EB" }} />
              <div style={{ textAlign: "center" }}>
                <Readout size={18} weight={700}>{fmtTime(last?.Arrival_DateTime)}</Readout>
                <div style={{ fontSize: 11, color: "#6B7280" }}>{last?.Destination}</div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Passengers */}
      {passengers.length > 0 && (
        <div style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 14, padding: "16px 18px", marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#6B7280", marginBottom: 10 }}>PASSENGERS</div>
          {passengers.map((p, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: i < passengers.length - 1 ? "1px solid #F3F4F6" : "none" }}>
              <span style={{ fontSize: 14, color: "#111827" }}>{p.title} {p.firstName} {p.lastName}</span>
              <span style={{ fontSize: 12, color: "#9CA3AF" }}>{["Adult", "Child", "Infant"][p.paxType]}</span>
            </div>
          ))}
          <div style={{ marginTop: 8, fontSize: 12, color: "#6B7280" }}>
            Contact: {contact.mobile} · {contact.email}
          </div>
        </div>
      )}

      {/* Fare breakdown */}
      <div style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 14, padding: "16px 18px", marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: "#6B7280", marginBottom: 10 }}>FARE SUMMARY</div>
        {repricedFlights.map((f, i) => {
          const fare = (f.Fares || [])[0];
          // Nextra's amount is already the grand total for every passenger. WeFlyGo's
          // FareDetails is PER PAX TYPE (see the matching note above) — a flat "* passengers
          // .length" over-counts as soon as pax types are mixed, same bug as legAmount above.
          const shown = provider === "nextra"
            ? (fare?.FareDetails || []).reduce((s, fd) => s + (fd.Total_Amount || 0), 0)
            : (fare?.FareDetails || []).reduce((s, fd) => {
                const countByType = [paxCounts.adults || 0, paxCounts.children || 0, paxCounts.infants || 0];
                return s + (fd.Total_Amount || 0) * (countByType[fd.PAX_Type] ?? 0);
              }, 0);
          return (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13, color: "#374151" }}>
              <span>{(f.Segments || [])[0]?.Origin} → {(f.Segments || [])[(f.Segments || []).length - 1]?.Destination} ({passengers.length} pax)</span>
              <Readout size={13} weight={600}>₹{shown.toLocaleString("en-IN")}</Readout>
            </div>
          );
        })}
        <div style={{ borderTop: "1.5px solid #E5E7EB", marginTop: 10, paddingTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: AIR.navy }}>Total Amount</span>
          <Readout size={17} weight={700} color={AIR.navy}>₹{totalAmount.toLocaleString("en-IN")}</Readout>
        </div>
      </div>

      {/* MPIN */}
      <div style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 14, padding: "18px", marginBottom: 16 }}>
        <label style={{ display: "block", fontWeight: 700, fontSize: 14, color: "#1E3A5F", marginBottom: 8 }}>
          Enter MPIN to Confirm Payment
        </label>
        <input
          type="password" value={mpin} maxLength={6}
          onChange={(e) => setMpin(e.target.value.replace(/\D/g, ""))}
          onFocus={() => setMpinFocused(true)} onBlur={() => setMpinFocused(false)}
          placeholder="● ● ● ● ● ●"
          style={{
            width: "100%", height: 50, borderRadius: 10, textAlign: "center", letterSpacing: 6, fontSize: 20,
            border: `2px solid ${mpinFocused ? "#3B82F6" : "#E5E7EB"}`, outline: "none", boxSizing: "border-box",
            boxShadow: mpinFocused ? "0 0 0 3px rgba(59,130,246,0.1)" : "none",
          }}
        />
        <div style={{ marginTop: 6, fontSize: 12, color: "#9CA3AF" }}>
          <Readout size={12} weight={600} color="#9CA3AF">₹{totalAmount.toLocaleString("en-IN")}</Readout> will be deducted from your wallet
        </div>
      </div>

      {error && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 16, color: "#DC2626", fontSize: 14 }}>
          {error}
        </div>
      )}

      <button
        onClick={handleConfirmPayment}
        disabled={isLoading}
        style={{
          width: "100%", height: 52, borderRadius: 12, border: "none",
          background: isLoading ? "#9CA3AF" : "#059669",
          color: "#fff", fontSize: 16, fontWeight: 700,
          cursor: isLoading ? "not-allowed" : "pointer",
          boxShadow: "0 4px 12px rgba(5,150,105,0.25)",
        }}
      >
        {paying ? "Processing payment..." : ticketing ? "Issuing ticket..." : nextraBooking ? "Booking & charging wallet..." : `Pay ₹${totalAmount.toLocaleString("en-IN")} & Issue Ticket`}
      </button>

      <div style={{ textAlign: "center", marginTop: 10, fontSize: 12, color: "#9CA3AF" }}>
        Secured payment · Amount deducted from Bingopay wallet
      </div>
    </div>
  );
};

export default FlightConfirm;
